package org.gymly.ui.components

import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.fillMaxSize
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.heightIn
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.foundation.text.KeyboardOptions
import androidx.compose.material3.Button
import androidx.compose.material3.ButtonDefaults
import androidx.compose.material3.CircularProgressIndicator
import androidx.compose.material3.OutlinedButton
import androidx.compose.material3.OutlinedTextField
import androidx.compose.material3.OutlinedTextFieldDefaults
import androidx.compose.material3.Text
import androidx.compose.runtime.Composable
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.text.input.KeyboardType
import androidx.compose.ui.text.input.PasswordVisualTransformation
import androidx.compose.ui.text.input.VisualTransformation
import org.gymly.theme.GymlyColors
import org.gymly.theme.GymlyFont
import org.gymly.theme.GymlyRadius
import org.gymly.theme.GymlySpace
import org.gymly.theme.GymlyTouch

@Composable
fun GymlyTextField(
    value: String,
    onValueChange: (String) -> Unit,
    modifier: Modifier = Modifier,
    label: String? = null,
    placeholder: String? = null,
    singleLine: Boolean = true,
    minLines: Int = 1,
    isPassword: Boolean = false,
    keyboardType: KeyboardType = KeyboardType.Text,
) {
    val (visible, toggleVisible) = rememberPasswordVisible()
    OutlinedTextField(
        value = value,
        onValueChange = onValueChange,
        modifier = modifier
            .fillMaxWidth()
            .heightIn(min = if (singleLine) GymlyTouch.minHeight else GymlyTouch.minHeight),
        label = label?.let { { Text(it) } },
        placeholder = placeholder?.let { { Text(it, color = GymlyColors.TextMuted) } },
        singleLine = singleLine,
        minLines = minLines,
        visualTransformation = if (isPassword && !visible) PasswordVisualTransformation() else VisualTransformation.None,
        trailingIcon = if (isPassword) {
            { PasswordVisibilityToggle(visible = visible, onToggle = toggleVisible) }
        } else {
            null
        },
        keyboardOptions = KeyboardOptions(keyboardType = keyboardType),
        shape = RoundedCornerShape(GymlyRadius.card),
        colors = OutlinedTextFieldDefaults.colors(
            focusedBorderColor = GymlyColors.Primary,
            unfocusedBorderColor = GymlyColors.Outline,
        ),
    )
}

@Composable
fun GymlyPrimaryButton(
    text: String,
    onClick: () -> Unit,
    modifier: Modifier = Modifier,
    loading: Boolean = false,
    enabled: Boolean = true,
) {
    Button(
        onClick = onClick,
        modifier = modifier
            .fillMaxWidth()
            .heightIn(min = GymlyTouch.minHeight),
        enabled = enabled && !loading,
        shape = RoundedCornerShape(GymlyRadius.card),
        colors = ButtonDefaults.buttonColors(
            containerColor = GymlyColors.Primary,
            contentColor = GymlyColors.OnPrimary,
        ),
    ) {
        if (loading) {
            CircularProgressIndicator(color = GymlyColors.OnPrimary)
        } else {
            Text(text, fontWeight = FontWeight.Bold)
        }
    }
}

@Composable
fun GymlySecondaryButton(
    text: String,
    onClick: () -> Unit,
    modifier: Modifier = Modifier,
    loading: Boolean = false,
) {
    OutlinedButton(
        onClick = onClick,
        modifier = modifier
            .fillMaxWidth()
            .heightIn(min = GymlyTouch.minHeight),
        enabled = !loading,
        shape = RoundedCornerShape(GymlyRadius.card),
    ) {
        if (loading) {
            CircularProgressIndicator(color = GymlyColors.Primary)
        } else {
            Text(text, fontWeight = FontWeight.Bold, color = GymlyColors.Primary)
        }
    }
}

@Composable
fun GymlyErrorText(message: String, modifier: Modifier = Modifier) {
    Box(
        modifier = modifier
            .fillMaxWidth()
            .padding(bottom = GymlySpace.md),
    ) {
        Text(
            text = message,
            color = GymlyColors.Error,
            fontSize = GymlyFont.small,
            modifier = Modifier
                .fillMaxWidth()
                .padding(GymlySpace.md),
        )
    }
}

@Composable
fun GymlyLoadingCenter() {
    Box(Modifier.fillMaxSize(), contentAlignment = Alignment.Center) {
        CircularProgressIndicator(color = GymlyColors.Primary)
    }
}

@Composable
fun FieldLabel(text: String) {
    Text(
        text = text,
        fontSize = GymlyFont.small,
        fontWeight = FontWeight.SemiBold,
        modifier = Modifier.padding(bottom = GymlySpace.xs),
    )
}
